import { useMemo, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, GridSizeChangedEvent } from 'ag-grid-community'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { Users, ArrowRightLeft, Trash2, Snowflake } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/utils/cn'
import { formatDateShort } from '@/utils/formatters'
import { formatUserDisplayName } from '@/utils/formatters'
import { getStatusBadgeClassName, getStatusLabel } from '@/utils/statusConfig'
import { FrozenIndicator } from '@/components/common/FrozenIndicator'
import type { ApiPlanSummary, ApiPlanStatus } from '@/types'

ModuleRegistry.registerModules([AllCommunityModule])

function TextCell(props: CustomCellRendererProps<ApiPlanSummary>) {
  return (
    <span className="text-sm text-foreground line-clamp-2">
      {props.value as string}
    </span>
  )
}

function StatusBadgeCell(props: CustomCellRendererProps<ApiPlanSummary>) {
  const status = props.value as ApiPlanStatus
  if (!status) return null

  return (
    <div className="flex items-center gap-1.5 pr-2">
      <Badge
        variant="outline"
        className={cn(
          'border-transparent font-medium',
          getStatusBadgeClassName(status)
        )}
      >
        {getStatusLabel(status)}
      </Badge>
      {props.data?.is_frozen && <FrozenIndicator variant="icon" />}
    </div>
  )
}

function OwnerCell(props: CustomCellRendererProps<ApiPlanSummary>) {
  const plan = props.data
  if (!plan) return null

  return (
    <span className="text-sm text-foreground line-clamp-2">
      {formatUserDisplayName(plan.owner)}
    </span>
  )
}

function DateCell(props: CustomCellRendererProps<ApiPlanSummary>) {
  const value = props.value as string
  if (!value) return null

  return (
    <span className="text-sm text-foreground">{formatDateShort(value)}</span>
  )
}

interface ActionsCellProps extends CustomCellRendererProps<ApiPlanSummary> {
  onManageShares: (plan: ApiPlanSummary) => void
  onTransferOwnership: (plan: ApiPlanSummary) => void
  onDeletePlan: (plan: ApiPlanSummary) => void
  onToggleFreeze: (plan: ApiPlanSummary) => void
}

function ActionsCell(props: ActionsCellProps) {
  const plan = props.data
  if (!plan) return null

  const { onManageShares, onTransferOwnership, onDeletePlan, onToggleFreeze } =
    props
  const isFrozen = !!plan.is_frozen

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center justify-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 cursor-pointer"
              onClick={() => onManageShares(plan)}
            >
              <Users className="h-4 w-4 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Manage Shares</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 cursor-pointer"
              onClick={() => onTransferOwnership(plan)}
            >
              <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Transfer Ownership</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 cursor-pointer"
              onClick={() => onToggleFreeze(plan)}
            >
              <Snowflake
                className={cn(
                  'h-4 w-4',
                  isFrozen ? 'text-blue-600' : 'text-muted-foreground'
                )}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isFrozen ? 'Unfreeze Plan' : 'Freeze Plan'}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn('h-8 w-8', isFrozen ? '' : 'cursor-pointer')}
              disabled={isFrozen}
              onClick={() => onDeletePlan(plan)}
            >
              <Trash2
                className={cn(
                  'h-4 w-4',
                  isFrozen ? 'text-muted-foreground' : 'text-destructive'
                )}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isFrozen ? 'Unfreeze the plan before deleting it' : 'Delete Plan'}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  )
}

interface AssortmentAccessGridProps {
  plans: ApiPlanSummary[]
  onManageShares: (plan: ApiPlanSummary) => void
  onTransferOwnership: (plan: ApiPlanSummary) => void
  onDeletePlan: (plan: ApiPlanSummary) => void
  onToggleFreeze: (plan: ApiPlanSummary) => void
  loading?: boolean
}

export function AssortmentAccessGrid({
  plans,
  onManageShares,
  onTransferOwnership,
  onDeletePlan,
  onToggleFreeze,
  loading,
}: AssortmentAccessGridProps) {
  const columnDefs: ColDef<ApiPlanSummary>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Plan Name',
        flex: 2,
        minWidth: 200,
        cellRenderer: TextCell,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 120,
        maxWidth: 150,
        cellRenderer: StatusBadgeCell,
      },
      {
        field: 'owner',
        headerName: 'Owner',
        flex: 1.5,
        minWidth: 150,
        cellRenderer: OwnerCell,
      },
      {
        field: 'modified_at',
        headerName: 'Last Modified',
        flex: 1,
        minWidth: 140,
        maxWidth: 160,
        cellRenderer: DateCell,
      },
      {
        headerName: 'Actions',
        flex: 1,
        minWidth: 180,
        maxWidth: 200,
        cellClass: 'ag-center-aligned-cell',
        headerClass: 'ag-center-aligned-header',
        cellRenderer: ActionsCell,
        cellRendererParams: {
          onManageShares,
          onTransferOwnership,
          onDeletePlan,
          onToggleFreeze,
        },
      },
    ],
    [onManageShares, onTransferOwnership, onDeletePlan, onToggleFreeze]
  )

  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit()
    }, 10)
  }, [])

  return (
    <div className="w-full">
      <div className="border border-[#e7e7e7] bg-white overflow-hidden">
        <div className="ag-theme-michaels w-full">
          <AgGridReact<ApiPlanSummary>
            rowData={plans}
            columnDefs={columnDefs}
            theme="legacy"
            domLayout="autoHeight"
            rowHeight={68}
            headerHeight={48}
            loading={loading}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            onGridSizeChanged={onGridSizeChanged}
            overlayNoRowsTemplate="<span>No plans found</span>"
            suppressCellFocus={true}
            suppressRowHoverHighlight={false}
            suppressColumnVirtualisation={true}
            suppressMovableColumns={true}
          />
        </div>
      </div>
    </div>
  )
}
